import _ from 'lodash';
import { fromJS, Map, OrderedMap } from 'immutable';
import { BaseStore } from 'fluxible/addons';
import keyMirror from 'keymirror';

export const RequestStates = keyMirror({
  Loading: null,
  Done: null,
  Error: null
});

class PaginatedStore extends BaseStore {
  static storeName = 'PaginatedStore';
  static handlers = {};
  static defaultPagination = { offset: 0, limit: 30 };
  static defaultSorter = undefined;
  static entityModel = undefined;

  constructor(dispatcher) {
    super(dispatcher);

    this.reset();
  }

  reset() {
    this.entities = new Map(); // All
    this.orderedEntities = new OrderedMap(); // Current ordered map corresponding with filters/pagination
    this.entitiesPendingRemoval = new Map(); // Temp storage

    this.currentFilters = [];
    this.currentPagination = this.constructor.defaultPagination;
    this.currentSorter = this.constructor.defaultSorter;

    this.currentEntityId = null;

    this.requestStatusByEntityId = new Map();
    this.loading = false;
  }

  logError(error) {
    if(typeof Bugsnag !== 'undefined') {
      Bugsnag.notifyException(error, error.name || 'Error', {metadata: error.metadata} || {});
    }
  }

  createEntityStart() {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set('NEW', RequestStates.Loading);
    this.emitChange();
  }

  createEntitySuccess(entity) {
    let id = entity.get('id');
    this.orderedEntities = (new OrderedMap({[id]: entity})).merge(this.orderedEntities);
    this.entities = this.entities.set(id, entity);
    this.requestStatusByEntityId = this.requestStatusByEntityId.set('NEW', RequestStates.Done);
    this.emitChange();
  }

  createEntityFailure(data) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set('NEW', RequestStates.Error);
    console.log(data.error);
    this.logError(data.error);
    this.emitChange();
  }

  fetchEntityStart(entityId) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(entityId, RequestStates.Loading);
    this.emitChange();
  }

  fetchEntitySuccess(entity) {
    let id = entity.get('id');
    if(this.orderedEntities.get(id)) {
      this.orderedEntities = this.orderedEntities.set(id, entity);
    }
    this.entities = this.entities.set(id, entity);
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(id, RequestStates.Done);
    this.emitChange();
  }

  fetchEntityFailure(data) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(data.entityId, RequestStates.Error);
    console.log(data.error);
    this.logError(data.error);
    this.emitChange();
  }

  listEntitiesStart(data) {
    if(data.forceRetry || !_.isEqual(data.sorter, this.currentSorter) || !_.isEqual(data.filters, this.currentFilters)) {
      this.orderedEntities = new OrderedMap();
      this.currentPagination = data.pagination || this.constructor.defaultPagination;
      this.currentSorter = data.sorter || this.constructor.defaultSorter;
      this.currentFilters = data.filters || [];
      this.loading = true;
    } else if(data.pagination.offset !== this.currentPagination.offset || data.pagination.limit !== this.currentPagination.limit) {
      this.currentPagination = data.pagination;
      this.loading = true;
    }
    this.emitChange();
  }

  listEntitiesSuccess(data) {
    if(!_.isEqual(data.sorter, this.currentSorter) || !_.isEqual(data.filters, this.currentFilters)) {
      return;
    }

    this.loading = false;
    this.orderedEntities = this.orderedEntities.merge(data.entities);

    this.entities = this.entities.withMutations((mutableMap) => {
      data.entities.forEach((entity) => {
        mutableMap.set(entity.id, entity);
      });
    });

    this.emitChange();
  }

  listEntitiesFailure(data) {
    // TODO
    console.log(data.error);
    this.logError(data.error);
  }

  deleteEntityStart(entityId) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(entityId, RequestStates.Loading);
    let entity = this.entities.get(entityId);
    if(entity) {
      // Store to be revived should the request fail
      this.entitiesPendingRemoval = this.entitiesPendingRemoval.set(entityId, entity);
    }
    this.entities = this.entities.delete(entityId);
    this.orderedEntities = this.orderedEntities.delete(entityId); // Order is lost
    this.emitChange();
  }

  deleteEntitySuccess(entityId) {
    this.entitiesPendingRemoval = this.entitiesPendingRemoval.delete(entityId);
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(entityId, RequestStates.Done);
    this.emitChange();
  }

  deleteEntityFailure(data) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(data.entityId, RequestStates.Error);
    let entity = this.entitiesPendingRemoval.get(data.entityId);
    if(entity) {
      this.entities = this.entities.set(data.entityId, entity);
      this.orderedEntities = this.orderedEntities.set(data.entityId, entity);
    }
    this.entitiesPendingRemoval = this.entitiesPendingRemoval.delete(data.entityId);
    console.log(data.error);
    this.logError(data.error);
    this.emitChange();
  }

  updateEntityStart(entityId) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(entityId, RequestStates.Loading);
    this.emitChange();
  }

  updateEntitySuccess(entity) {
    let id = entity.get('id');
    if(this.orderedEntities.get(id)) {
      this.orderedEntities = this.orderedEntities.set(id, entity);
    }
    this.entities = this.entities.set(id, entity);
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(id, RequestStates.Done);
    this.emitChange();
  }

  updateEntityFailure(data) {
    this.requestStatusByEntityId = this.requestStatusByEntityId.set(data.entityId, RequestStates.Error);
    console.log(data.error);
    this.logError(data.error);
    this.emitChange();
  }

  getEntityById(id) {
    return this.entities.get(id) || this.orderedEntities.get(id);
  }

  getCurrentEntity() {
    return this.entities.get(this.currentEntityId) || this.orderedEntities.get(this.currentEntityId);
  }

  getCurrentEntityId() {
    return this.currentEntityId;
  }

  setCurrentEntity(entityId) {
    this.currentEntityId = entityId;
    this.emitChange();
  }

  getRequestStatusByEntityId(entityId) {
    return this.requestStatusByEntityId.get(entityId);
  }

  hasResultsForListRequest(sorter, filters) {
    return _.isEqual(sorter, this.currentSorter) && _.isEqual(filters, this.currentFilters) && this.orderedEntities.size;
  }

  getCurrentState() {
    return {
      entities: this.orderedEntities,
      currentEntityId: this.currentEntityId,
      filters: this.currentFilters,
      sorter: this.currentSorter,
      pagination: this.currentPagination,
      loading: this.loading
    };
  }

  dehydrate() {
    return {
      orderedEntities: this.orderedEntities.toList().toJS(),
      entities: this.entities.toJS(),
      currentFilters: this.currentFilters,
      currentPagination: this.currentPagination,
      currentSorter: this.currentSorter,
      currentEntityId: this.currentEntityId,
      requestStatusByEntityId: this.requestStatusByEntityId.toJS()
    };
  }

  rehydrate(state) {
    this.entities = _.reduce(state.entities, (map, entity, id) => {
      let model = this.constructor.entityModel ? new this.constructor.entityModel(entity) : fromJS(entity);
      return map.set(id, model);
    }, new Map());

    this.orderedEntities = _.reduce(state.orderedEntities, (map, entity) => {
      let model = this.entities.get(entity.id);
      if(!model) {
        model = this.constructor.entityModel ? new this.constructor.entityModel(entity) : fromJS(entity);
      }
      return map.set(entity.id, model);
    }, new OrderedMap());

    this.currentFilters = state.currentFilters;
    this.currentPagination = state.currentPagination;
    this.currentSorter = state.currentSorter;
    this.currentEntityId = state.currentEntityId;
    this.requestStatusByEntityId = fromJS(state.requestStatusByEntityId);
  }
}

export default PaginatedStore;



// WEBPACK FOOTER //
// banquet/stores/PaginatedStore.js